import type { PrepListItem } from '../../api/prep'
import type { SelectionContext } from '../../types/prep'

export interface VisibleItemsParams {
  items: readonly PrepListItem[]
  myOnly: boolean
  myIds: ReadonlySet<number>
}

export const getVisibleMyItems = ({ items, myOnly, myIds }: VisibleItemsParams): PrepListItem[] => {
  if (!Array.isArray(items)) {
    return []
  }

  if (!myOnly) {
    return items as PrepListItem[]
  }

  if (items.length === 0) {
    return []
  }

  return items.filter((item) => myIds.has(item.id) || item.is_in_my_prep)
}

export type SelectionResolution = number | null | undefined

export interface SelectNextAfterRemovalParams {
  selectedId: number | null
  removedId: number
  visibleItems: readonly PrepListItem[]
  removalIndex?: number
  lastKnownIndex?: number | null
}

export const selectNextAfterRemoval = ({
  selectedId,
  removedId,
  visibleItems,
  removalIndex,
  lastKnownIndex,
}: SelectNextAfterRemovalParams): SelectionResolution => {
  if (selectedId == null || selectedId !== removedId) {
    return undefined
  }

  if (!Array.isArray(visibleItems) || visibleItems.length === 0) {
    return null
  }

  const withoutRemoved = visibleItems.filter((item) => item.id !== removedId)

  if (withoutRemoved.length === 0) {
    return null
  }

  const fallbackIndex = (() => {
    if (typeof lastKnownIndex === 'number' && lastKnownIndex >= 0) {
      return Math.min(lastKnownIndex, withoutRemoved.length - 1)
    }

    if (typeof removalIndex === 'number' && removalIndex >= 0) {
      return Math.min(removalIndex, withoutRemoved.length - 1)
    }

    return 0
  })()

  return withoutRemoved[fallbackIndex]?.id ?? withoutRemoved[0]?.id ?? null
}

interface MyOnlySelectionParams extends SelectionContext {
  myOnly: boolean
  ready: boolean
  lastKnownIndex: number | null
}

export const resolveSelectionForMyOnly = ({
  myOnly,
  ready,
  visibleItems,
  selectedId,
  lastClickedId,
  lastKnownIndex,
}: MyOnlySelectionParams): SelectionResolution => {
  if (!myOnly || !ready) {
    return undefined
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.assert(Array.isArray(visibleItems), '[prep] visibleItems must be an array')
  }

  if (!Array.isArray(visibleItems)) {
    return undefined
  }

  if (visibleItems.length === 0) {
    return selectedId !== null ? null : undefined
  }

  if (lastClickedId != null) {
    const clickedMatch = visibleItems.find((item) => item.id === lastClickedId)
    if (clickedMatch) {
      return clickedMatch.id === selectedId ? undefined : clickedMatch.id
    }
  }

  if (selectedId != null) {
    const exists = visibleItems.some((item) => item.id === selectedId)
    if (exists) {
      return undefined
    }
  }

  const fallbackIndex =
    typeof lastKnownIndex === 'number' && lastKnownIndex >= 0
      ? Math.min(lastKnownIndex, visibleItems.length - 1)
      : 0

  return visibleItems[fallbackIndex]?.id ?? visibleItems[0]?.id ?? null
}

interface SingleLayoutSelectionParams extends SelectionContext {
  isSingleLayout: boolean
  ready: boolean
  isMyOnly: boolean
}

export const resolveSelectionForSingleLayout = ({
  isSingleLayout,
  ready,
  visibleItems,
  selectedId,
  isMyOnly,
  lastClickedId,
}: SingleLayoutSelectionParams): SelectionResolution => {
  if (!isSingleLayout || isMyOnly || !ready) {
    return undefined
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.assert(Array.isArray(visibleItems), '[prep] visibleItems must be an array')
  }

  if (!Array.isArray(visibleItems)) {
    return undefined
  }

  if (visibleItems.length === 0) {
    return selectedId !== null ? null : undefined
  }

  if (lastClickedId != null) {
    const clickedMatch = visibleItems.find((item) => item.id === lastClickedId)
    if (clickedMatch) {
      return clickedMatch.id === selectedId ? undefined : clickedMatch.id
    }
  }

  if (selectedId != null) {
    const exists = visibleItems.some((item) => item.id === selectedId)
    if (exists) {
      return undefined
    }
  }

  return visibleItems[0]?.id ?? null
}

export const shouldOpenDrawerOnMobile = ({
  isDesktop,
  selectedId,
}: {
  isDesktop: boolean
  selectedId: number | null
}): boolean => {
  if (isDesktop) {
    return false
  }

  return selectedId !== null
}
